/* eslint-disable @typescript-eslint/no-unused-vars */
import { microApi } from 'app/services/api';
import { ListResponse } from 'app/types';
import qs from 'qs';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import { result } from 'lodash';
import { TagDescription } from '@reduxjs/toolkit/dist/query/endpointDefinitions';

const END_POINTS = 'inspections/criterias/templates';
export const templateApi = microApi.injectEndpoints({
  endpoints: (build) => ({
    getTemplates: build.query<
      {
        data: Array<any>;
        pagination: {
          page: number;
          pageSize: number;
          pages: number;
          total: number;
        };
      },
      {
        current: number;
        pageSize: number;
        status?: any;
        name?: any;
        productProfileId?: any;
      }
    >({
      query: (params) => {
        const queryString = qs.stringify(
          {
            pageNo: params.current,
            pageSize: params.pageSize,
            // ...pick(params, ['status', 'name', 'productProfileId', 'excludes'])
            ...pick(params, [
              'status',
              'name',
              'keywords',
              'orderBy',
              'productProfileId',
              'createdAtFrom',
              'createdAtTo',
              'updatedAtFrom',
              'updatedAtTo',
              'excludes'
            ])
          },
          {
            encodeValuesOnly: false,
            indices: false
          }
        );
        return {
          url: `/${END_POINTS}${queryString ? '?' + queryString : ''}`,
          method: 'GET'
        };
      },
      transformResponse: (baseQueryReturnValue: ListResponse<any>, _, args) => {
        if (isEmpty(baseQueryReturnValue.data))
          return {
            pagination: {
              page: args.current,
              pageSize: args.pageSize,
              pages: 0,
              total: 0
            },
            data: []
          };
        return {
          pagination: baseQueryReturnValue.data.pagination,
          data: (baseQueryReturnValue as any).data?.list ?? []
        };
      },
      providesTags: () => [{ type: 'Template', id: 'LIST' }]
    }),
    getTemplate: build.query<any, string | number>({
      query: (templateId: number) => {
        return {
          url: `/${END_POINTS}/${templateId}`,
          method: 'GET'
        };
      },
      transformResponse: (baseQueryReturnValue: {
        statusCode: number;
        data: any;
      }) => {
        return baseQueryReturnValue.data;
      }
    }),
    getDefaultTemplate: build.query<any, null>({
      query: () => {
        return {
          url: `${END_POINTS}/default`,
          method: 'GET'
        };
      },
      transformResponse: (baseQueryReturnValue: ListResponse<any>) => {
        return (baseQueryReturnValue as any).data;
      }
    }),
    createTemplate: build.mutation<
      any,
      {
        name: string;
        productIds: Array<number>;
        scheme: {
          [key: string]: any;
        };
      }
    >({
      query: (data) => {
        return {
          url: `/admin/${END_POINTS}`,
          method: 'POST',
          body: {
            ...data,
            scheme: JSON.stringify(data.scheme)
          }
        };
      },
      transformResponse: (baseQueryReturnValue: {
        statusCode: number;
        data: any;
      }) => {
        return baseQueryReturnValue.data;
      }
    }),
    updateTemplate: build.mutation<
      any,
      {
        templateId: number;
        data: {
          name: string;
          productIds: Array<number>;
          scheme: {
            [key: string]: any;
          };
        };
      }
    >({
      query: (data) => {
        const { templateId, data: payload } = data;
        return {
          url: `/admin/${END_POINTS}/${templateId}`,
          method: 'PUT',
          body: {
            ...payload,
            scheme: JSON.stringify(payload.scheme)
          }
        };
      },
      transformResponse: (baseQueryReturnValue: {
        statusCode: number;
        data: any;
      }) => {
        return baseQueryReturnValue.data;
      }
    }),
    getTemplateByPOItemId: build.query<
      {
        data: Array<any>;
      },
      {
        poItemId: string | number;
      }
    >({
      query: (params) => {
        const queryString = qs.stringify(
          { ...params, pageNo: 1, pageSize: 9999 },
          {
            encodeValuesOnly: false,
            indices: false
          }
        );
        return {
          url: `/${END_POINTS}${queryString ? '?' + queryString : ''}`,
          method: 'GET'
        };
      },
      transformResponse: (baseQueryReturnValue: ListResponse<any>, _, args) => {
        return {
          data: (baseQueryReturnValue as any).data?.list ?? []
        };
      }
    }),
    activeTemplate: build.mutation({
      query: (id) => {
        return {
          url: `${END_POINTS}/${id}/active`,
          method: 'PUT',
          body: {}
        };
      },
      transformResponse: (baseQueryReturnValue: {
        statusCode: number;
        data: any;
      }) => {
        return baseQueryReturnValue.data;
      }
    }),
    inActiveTemplate: build.mutation({
      query: (id) => {
        return {
          url: `${END_POINTS}/${id}/inactive`,
          method: 'PUT',
          body: {}
        };
      },
      transformResponse: (baseQueryReturnValue: {
        statusCode: number;
        data: any;
      }) => {
        return baseQueryReturnValue.data;
      }
    }),
    setDefaultTemplate: build.mutation({
      query: (id) => {
        return {
          url: `admin/${END_POINTS}/${id}/default`,
          method: 'PUT',
          body: {}
        };
      },
      transformResponse: (baseQueryReturnValue: {
        statusCode: number;
        data: any;
      }) => {
        return baseQueryReturnValue.data;
      },
      invalidatesTags: (): TagDescription<
        'Auth' | 'Inspection' | 'Template'
      >[] => {
        return [{ type: 'Template', id: 'LIST' }];
      }
    })
  })
});

export const {
  useLazyGetTemplatesQuery,
  useLazyGetDefaultTemplateQuery,
  useGetDefaultTemplateQuery,
  useLazyGetTemplateQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useLazyGetTemplateByPOItemIdQuery,
  useActiveTemplateMutation,
  useInActiveTemplateMutation,
  useSetDefaultTemplateMutation
}: typeof templateApi = templateApi;
